import { Network } from "../Network";
import { WarehouseEndPoints } from "apis/EndPoints/WarehouseEndPoints";

const normalizeWarehouseItem = (item = {}) => {
  if (!item || typeof item !== "object") {
    return item;
  }

  return {
    ...item,
    itemVendor: item?.itemVendor ?? "",
  };
};

const normalizeWarehouseItemList = (items) => {
  return Array.isArray(items) ? items.map(normalizeWarehouseItem) : items;
};

const normalizeTransferRecord = (transfer = {}) => {
  if (!transfer || typeof transfer !== "object") {
    return transfer;
  }

  return {
    ...transfer,
    items: normalizeWarehouseItemList(transfer?.items),
  };
};

const normalizeTransferRecordList = (transfers) => {
  return Array.isArray(transfers)
    ? transfers.map(normalizeTransferRecord)
    : transfers;
};

const normalizeTransferRequestsResponse = (response = {}) => {
  if (!response || typeof response !== "object") {
    return response;
  }

  return {
    ...response,
    transfersHistory:
      response?.transfersHistory &&
      typeof response.transfersHistory === "object"
        ? {
            ...response.transfersHistory,
            pending: normalizeTransferRecordList(
              response?.transfersHistory?.pending,
            ),
            history: normalizeTransferRecordList(
              response?.transfersHistory?.history,
            ),
          }
        : response?.transfersHistory,
  };
};

const normalizeTransfersToProjectInventoryHistoryResponse = (response = {}) => {
  if (!response || typeof response !== "object") {
    return response;
  }

  return {
    ...response,
    inProcessingTransfers: normalizeTransferRecordList(
      response?.inProcessingTransfers,
    ),
    completedTransfers: normalizeTransferRecordList(response?.completedTransfers),
  };
};

const normalizeWarehouseItemPayload = (values) => {
  if (Array.isArray(values)) {
    return values.map(normalizeWarehouseItemPayload);
  }

  if (!values || typeof values !== "object") {
    return values;
  }

  return {
    ...values,
    itemVendor: values?.itemVendor ?? "",
    note: values?.note ?? "",
  };
};

export class WarehouseServices {
  static inventoryPageAccessLevel(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.inventoryPageAccessLevel.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.inventoryPageAccessLevel.method,
      },
    );
  }
  static getTransferRequests(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getTransferRequests.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getTransferRequests.method,
      },
    ).then(normalizeTransferRequestsResponse);
  }

  static getTransferDrafts(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getTransferDrafts.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getTransferDrafts.method,
      },
    ).then(normalizeTransferRecordList);
  }

  static getWarehouseItems(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getWarehouseItems.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getWarehouseItems.method,
      },
    ).then(normalizeWarehouseItemList);
  }
  static getPurchaseOrderLineItemsFromProcore(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getPurchaseOrderLineItemsFromProcore.url(
        data?.company,
        data?.projectID,
        data?.purchaseOrderID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getPurchaseOrderLineItemsFromProcore.method,
      },
    );
  }
  static getWorkBreakDownStructureCode(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getWorkBreakDownStructureCode.url(
        data?.company,
        data?.projectID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getWorkBreakDownStructureCode.method,
      },
    );
  }
  static transfersToProjectInventoryHistory(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.transfersToProjectInventoryHistory.url(
        data?.company,
        data?.projectID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.transfersToProjectInventoryHistory.method,
      },
    ).then(normalizeTransfersToProjectInventoryHistoryResponse);
  }
  static getPurchaseOrdersInKoncurent(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getPurchaseOrdersInKoncurent.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getPurchaseOrdersInKoncurent.method,
      },
    );
  }
  static getWarehouseOverview(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getWarehouseOverview.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getWarehouseOverview.method,
      },
    );
  }
  static getInventorySettings(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getInventorySettings.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getInventorySettings.method,
      },
    );
  }
  static getInventoryPurchaseHistory(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getInventoryPurchaseHistory.url(
        data?.company,
        data?.itemID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getInventoryPurchaseHistory.method,
      },
    );
  }

  static inventoryItemsHistory(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.inventoryItemsHistory.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.inventoryItemsHistory.method,
        body: JSON.stringify({
          sortBy: data?.sortBy || "Descending",
        }),
      },
    );
  }

  static inventoryItemsHistoryDetailed(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.inventoryItemsHistoryDetailed.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.inventoryItemsHistoryDetailed.method,
        body: JSON.stringify({
          itemID: data?.itemID || "",
        }),
      },
    );
  }

  static inventoryItemsNoMovement30Days(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.inventoryItemsNoMovement30Days.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.inventoryItemsNoMovement30Days.method,
        body: JSON.stringify({}),
      },
    );
  }

  static inventoryItemsNeverMoved(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.inventoryItemsNeverMoved.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.inventoryItemsNeverMoved.method,
        body: JSON.stringify({}),
      },
    );
  }

  static inventoryItemsStockVsTransferDrafts(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.inventoryItemsStockVsTransferDrafts.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.inventoryItemsStockVsTransferDrafts.method,
        body: JSON.stringify({}),
      },
    );
  }

  static updatePurchaseOrderLineItemInProcore(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updatePurchaseOrderLineItemInProcore.url(
        data?.company,
        data?.projectID,
        data?.purchaseOrderID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updatePurchaseOrderLineItemInProcore.method,
        body: JSON.stringify(data?.lineItems),
      },
    );
  }

  static postPurchaseOrderLineItemToProcore(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.postPurchaseOrderLineItemToProcore.url(
        data?.company,
        data?.projectID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.postPurchaseOrderLineItemToProcore.method,
        body: JSON.stringify(data?.lineItems),
      },
    );
  }

  static updateWarehouseItem(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updateWarehouseItem.url(data?.company, data?.itemID),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updateWarehouseItem.method,
        body: JSON.stringify(normalizeWarehouseItemPayload(data?.values)),
      },
    );
  }

  static createWarehouseItem(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.createWarehouseItem.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.createWarehouseItem.method,
        body: JSON.stringify(normalizeWarehouseItemPayload(data?.values)),
      },
    ).then(normalizeWarehouseItem);
  }

  static transferInventoryItem(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.transferInventoryItems.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.transferInventoryItems.method,
        body: JSON.stringify(data?.values),
      },
    );
  }

  static transferDraft(data = {}) {
    return Network.fetch(WarehouseEndPoints.transferDraft.url(data?.company), {
      headers: {
        "Content-type": "application/json",
        Authorization: data?.code,
        From: data?.loginEmail,
      },
      method: WarehouseEndPoints.transferDraft.method,
      body: JSON.stringify(data?.values),
    });
  }

  static transferRequest(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.transferRequest.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.transferRequest.method,
        body: JSON.stringify(data?.values),
      },
    );
  }

  static sendKoncurentPurchaseOrdersToInventory(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.sendKoncurentPurchaseOrdersToInventory.url(
        data?.company,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method:
          WarehouseEndPoints.sendKoncurentPurchaseOrdersToInventory.method,
        body: JSON.stringify(data),
      },
    );
  }

  static markPurchaseOrderCompleted(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.markPurchaseOrderCompleted.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.markPurchaseOrderCompleted.method,
        body: JSON.stringify({
          completedPurchaseOrders: data?.completedPurchaseOrders || [],
        }),
      },
    );
  }

  static uploadWarehouseItems(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.uploadWarehouseItems.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.uploadWarehouseItems.method,
        body: JSON.stringify(data),
      },
    );
  }

  static postPurchaseOrderToProcore(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.postPurchaseOrderToProcore.url(
        data?.company,
        data?.projectID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.postPurchaseOrderToProcore.method,
        body: JSON.stringify(data?.Values),
      },
    );
  }

  static updatePurchaseOrderInProcore(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updatePurchaseOrderInProcore.url(
        data?.company,
        data?.projectID,
        data?.purchaseOrderID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updatePurchaseOrderInProcore.method,
        body: JSON.stringify(data?.Values),
      },
    );
  }

  static transferHistoryInventory(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.transferHistoryInventory.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.transferHistoryInventory.method,
      },
    ).then(normalizeTransferRecordList);
  }

  static updateTransferInventoryItem(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updateTransferInventoryItem.url(
        data?.company,
        data?.itemID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updateTransferInventoryItem.method,
        body: JSON.stringify(data),
      },
    );
  }

  static updateTransferDrafts(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updateTransferDrafts.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updateTransferDrafts.method,
        body: JSON.stringify(data?.values),
      },
    );
  }

  static approveTransferRequests(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.approveTransferRequests.url(
        data?.company,
        data?.itemID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.approveTransferRequests.method,
        body: JSON.stringify(data?.values),
      },
    );
  }

  static submitTransferDrafts(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.submitTransferDrafts.url(data?.company, data?.itemID),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.submitTransferDrafts.method,
        body: JSON.stringify(data?.values),
      },
    );
  }

  static createInventorySettings(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.createInventorySettings.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.createInventorySettings.method,
        body: JSON.stringify(data),
      },
    );
  }

  static deleteInventorySettings(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteInventorySettings.url(
        data?.company,
        data?.itemID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteInventorySettings.method,
        body: JSON.stringify(data),
      },
    );
  }

  static updateInventorySettings(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updateInventorySettings.url(data?.company),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updateInventorySettings.method,
        body: JSON.stringify(data),
      },
    );
  }

  static deleteWarehouseItem(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteWarehouseItem.url(data?.company, data?.itemID),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteWarehouseItem.method,
      },
    );
  }

  static deleteTransferDrafts(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteTransferDrafts.url(
        data?.company,
        data?.transferID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteTransferDrafts.method,
      },
    );
  }

  static deleteTransferDraftsItems(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteTransferDraftsItems.url(
        data?.company,
        data?.transferID,
        data?.transferItemID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteTransferDraftsItems.method,
      },
    );
  }

  static getProjectPurchaseOrdersFromProcore(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getProjectPurchaseOrdersFromProcore.url(
        data?.company,
        data?.projectID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getProjectPurchaseOrdersFromProcore.method,
      },
    );
  }

  static savePurchaseOrder(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.savePurchaseOrder.url(data?.company, data?.projectID),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.savePurchaseOrder.method,
        body: JSON.stringify(data?.selectedItems),
      },
    );
  }

  static getJobSites(data = {}) {
    return Network.fetch(WarehouseEndPoints.getJobSites.url(data?.company), {
      headers: {
        "Content-type": "application/json",
        Authorization: data?.code,
        From: data?.loginEmail,
      },
      method: WarehouseEndPoints.getJobSites.method,
    });
  }

  static createJobSite(data = {}) {
    return Network.fetch(WarehouseEndPoints.createJobSite.url(data?.company), {
      headers: {
        "Content-type": "application/json",
        Authorization: data?.code,
        From: data?.loginEmail,
      },
      method: WarehouseEndPoints.createJobSite.method,
      body: JSON.stringify(data?.requestData),
    });
  }

  static updateJobSite(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updateJobSite.url(
        data?.company,
        data?.requestData?.jobSiteID,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updateJobSite.method,
        body: JSON.stringify(data?.requestData),
      },
    );
  }
  static deleteJobSite(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteJobSite.url(data?.company, data?.jobSiteID),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteJobSite.method,
        // body: JSON.stringify(data),
      },
    );
  }

  static getDataToCreateUpdateCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getDataToCreateUpdateCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getDataToCreateUpdateCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }

  static createCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.createCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.createCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }

  static updateCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.updateCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.updateCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }

  static deleteCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }

  static cloneCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.cloneCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.cloneCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }
  static shareCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.shareCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.shareCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }
  static deleteSharedCustomReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.deleteSharedCustomReport.url(
        data?.company,
        data?.project,
        data?.reportName,
        data?.reportTabName,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.deleteSharedCustomReport.method,
        body: JSON.stringify(data),
      },
    );
  }
  static getReportDetails(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getReportDetails.url(
        data?.company,
        data?.project,
        data?.firstColumnName,
        data?.secondColumnName,
        data?.minValue,
        data?.maxValue,
        data?.sortBy,
        data?.status,
        data?.filteredColumns,
        data?.filterCombination,
        data?.subcontractorFilter,
        data?.reportRequestName,
        data?.responseFilter,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getReportDetails.method,
        body: JSON.stringify(data),
      },
    );
  }
  static getReportAccessLevel(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.getReportAccessLevel.url(data?.company, data?.project),
      {
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.getReportAccessLevel.method,
      },
    );
  }

  static modifyIssueDateReport(data = {}) {
    return Network.fetch(
      WarehouseEndPoints.modifyIssueDateReport.url(
        data?.company,
        data?.project,
      ),
      {
        headers: {
          "Content-type": "application/json",
          Authorization: data?.code,
          From: data?.loginEmail,
        },
        method: WarehouseEndPoints.modifyIssueDateReport.method,
        body: JSON.stringify(data?.editedData),
      },
    );
  }
}
